import { useEffect, useState } from 'react';
import { Maths } from 'helpers/maths';
import {
  getDefaultAreaType,
  AreaType
} from 'screens/Applications&WorkOrders/StepsLayout/Constants/areaTypeConstants';
import {
  calculateCoveragePercent,
  calculateAppliedAcreage,
  calculateTotalAppliedAcreage
} from 'screens/Applications&WorkOrders/StepsLayout/Components/PropertiesStep/helpers/cropZoneMath';
import { CropZoneRecord } from './types';
import { SortInfo, getSortedData } from './helpers';

const NUMBER_INPUT_LOWER_LIMIT = 0;

const useCropZonesTableState = <T extends CropZoneRecord>(
  data: T[],
  onTotalAcreageChange: (total: number) => void,
  onDataChange?: (data: T[]) => void
) => {
  const [tableData, setTableData] = useState(data);
  const [focusedInput, setFocusedInput] = useState<{
    type: 'acreage' | 'coverage';
    idx: number;
  } | null>(null);
  const [pendingEdits, setPendingEdits] = useState<{ [key: string]: string }>(
    {}
  );
  const [sortInfo, setSortInfo] = useState<SortInfo>({
    columnKey: '',
    order: null
  });
  const [currentPage, setCurrentPage] = useState(1);

  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingRow, setEditingRow] = useState<{
    index: number;
    record: T;
  } | null>(null);
  const [selectedAreaType, setSelectedAreaType] = useState<AreaType>(
    'reported'
  );
  const [isDropdownVisible, setIsDropdownVisible] = useState(false);

  useEffect(() => {
    if (!data) return;
    const newTableData = data.map(newZone => {
      const existingZone = tableData.find(item => item.id === newZone.id);
      if (existingZone) {
        return {
          ...existingZone,
          ...newZone,
          appliedAcreage: newZone.appliedAcreage,
          coveragePercent: newZone.coveragePercent
        };
      }
      return newZone;
    });
    setTableData(newTableData);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const updateRow = (idx: number, changes: Partial<CropZoneRecord>) => {
    const currentRow = (tableData[idx] as unknown) as Record<string, unknown>;
    const hasRealChange = (Object.entries(changes) as Array<
      [string, unknown]
    >).some(([key, value]) => currentRow[key] !== value);
    if (!hasRealChange) return;

    const updatedTableData = tableData.map((row, i) =>
      i === idx ? { ...row, ...changes } : row
    );
    setTableData(updatedTableData);

    onDataChange?.(updatedTableData);
    onTotalAcreageChange(calculateTotalAppliedAcreage(updatedTableData));
  };

  const getArea = (zone: T) =>
    zone.selectedAreaType === 'boundary' ? zone.boundaryArea! : zone.area;

  const handleAcreageBlur = (value: number, idx: number) => {
    if (value <= NUMBER_INPUT_LOWER_LIMIT) {
      // Falls back to recomputing from the row's stored coverage — unless
      // that's also non-positive, in which case there's nothing valid to
      // derive from on either side; no-op rather than recursing into
      // `handleCoverageBlur`, which would call back into this same branch
      // with the same stale values (infinite recursion).
      if (tableData[idx].coveragePercent <= NUMBER_INPUT_LOWER_LIMIT) return;
      // eslint-disable-next-line @typescript-eslint/no-use-before-define
      handleCoverageBlur(tableData[idx].coveragePercent, idx);
      return;
    }

    const zone = tableData[idx];
    const newCoverage = calculateCoveragePercent(value, getArea(zone));
    updateRow(idx, { appliedAcreage: value, coveragePercent: newCoverage });
  };

  const handleCoverageBlur = (value: number, idx: number) => {
    if (value <= NUMBER_INPUT_LOWER_LIMIT) {
      if (tableData[idx].appliedAcreage <= NUMBER_INPUT_LOWER_LIMIT) return;
      handleAcreageBlur(tableData[idx].appliedAcreage, idx);
      return;
    }

    const zone = tableData[idx];
    const newAcreage = calculateAppliedAcreage(value, getArea(zone));
    updateRow(idx, { coveragePercent: value, appliedAcreage: newAcreage });
  };

  const deleteRow = (idx: number) => {
    const newTableData = tableData.filter((_, i) => i !== idx);
    setTableData(newTableData);

    onDataChange?.(newTableData);
    onTotalAcreageChange(calculateTotalAppliedAcreage(newTableData));
  };

  const openEditModal = (index: number, record: T) => {
    setEditingRow({ index, record });
    setEditModalOpen(true);

    const areaType =
      record.selectedAreaType || getDefaultAreaType(record.reportedArea);
    setSelectedAreaType(areaType);
  };

  const closeEditModal = () => {
    if (editingRow) {
      const savedAreaType =
        editingRow.record.selectedAreaType ||
        getDefaultAreaType(editingRow.record.reportedArea);
      setSelectedAreaType(savedAreaType);
    }
    setEditModalOpen(false);
    setEditingRow(null);
  };

  const handleAreaTypeChange = (value: 'reported' | 'boundary') => {
    setSelectedAreaType(value);
  };

  const handleModalSave = () => {
    if (editingRow) {
      const { reportedArea, boundaryArea } = editingRow.record;
      const newAreaValue =
        selectedAreaType === 'reported' ? reportedArea : boundaryArea;

      const currentRow = tableData[editingRow.index];

      // Compare against the row's current area (via getArea, which reflects
      // the currently-selected area type) — not appliedAcreage, since
      // appliedAcreage is area × coveragePercent / 100 and would only equal
      // newAreaValue when coveragePercent happens to be 100. Comparing to
      // area correctly detects a no-op modal save and avoids a spurious
      // recalculation that would introduce floating-point drift into
      // appliedAcreage.
      if (newAreaValue === getArea(currentRow)) {
        const updatedTableData = [...tableData];
        updatedTableData[editingRow.index] = {
          ...updatedTableData[editingRow.index],
          selectedAreaType
        };

        setTableData(updatedTableData);
        onDataChange?.(updatedTableData);
      } else {
        const updatedTableData = [...tableData];
        const currentCoveragePercent =
          updatedTableData[editingRow.index].coveragePercent;

        const newAppliedAcreage = Maths.divideSafeRound(
          Maths.multiplyRound(newAreaValue || 0, currentCoveragePercent),
          100
        );

        updatedTableData[editingRow.index] = {
          ...updatedTableData[editingRow.index],
          area: newAreaValue || 0,
          appliedAcreage: newAppliedAcreage,
          selectedAreaType
        };

        setTableData(updatedTableData);
        onDataChange?.(updatedTableData);
        onTotalAcreageChange(calculateTotalAppliedAcreage(updatedTableData));
      }
    }
    closeEditModal();
  };

  const getDisplayValue = (
    value: number,
    type: 'acreage' | 'coverage',
    idx: number
  ) => {
    const key = `${type}-${idx}`;
    const isFocused = focusedInput?.type === type && focusedInput?.idx === idx;
    if (isFocused && pendingEdits[key] !== undefined) {
      return pendingEdits[key];
    }
    if (value !== undefined && value !== null) {
      return Number(value).toFixed(2);
    }
    return '';
  };

  const handleInputChange = (
    type: 'acreage' | 'coverage',
    idx: number,
    val: string
  ) => {
    setPendingEdits(prev => ({ ...prev, [`${type}-${idx}`]: val }));
  };

  const handleInputFocus = (
    type: 'acreage' | 'coverage',
    idx: number,
    rawValue: number | undefined
  ) => {
    setFocusedInput({ type, idx });
    setPendingEdits(prev => ({
      ...prev,
      [`${type}-${idx}`]: rawValue?.toString() ?? ''
    }));
  };

  const handleInputBlur = (
    type: 'acreage' | 'coverage',
    rowIdx: number,
    handler: (value: number, idx: number) => void
  ) => {
    const key = `${type}-${rowIdx}`;
    setFocusedInput(null);
    const val = Number(pendingEdits[key]);
    const originalValue =
      type === 'acreage'
        ? tableData[rowIdx].appliedAcreage
        : tableData[rowIdx].coveragePercent;

    // If the user clicked/tabbed into the input and left without changing
    // the value, short-circuit. The handler would otherwise recompute the
    // dependent field via floating-point math, which can differ from the
    // stored value by sub-decimal drift — that drift would then be detected
    // as a "real change" by updateRow and cascade through to
    // totalAppliedAcreage/products, silently rounding values the user never
    // touched.
    if (Number.isNaN(val) || val === originalValue) {
      setPendingEdits(prev => {
        const copy = { ...prev };
        delete copy[key];
        return copy;
      });
      return;
    }

    handler(val, rowIdx);
    setPendingEdits(prev => {
      const copy = { ...prev };
      delete copy[key];
      return copy;
    });
  };

  const handleTableChange = (_: any, __: any, sorter: any) => {
    setSortInfo({
      columnKey: sorter.columnKey,
      order: sorter.order
    });
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const sortedData = getSortedData(tableData, sortInfo);

  return {
    tableData,
    sortedData,
    sortInfo,
    currentPage,
    editModalOpen,
    editingRow,
    selectedAreaType,
    isDropdownVisible,
    setIsDropdownVisible,
    getDisplayValue,
    handleInputChange,
    handleInputFocus,
    handleInputBlur,
    handleAcreageBlur,
    handleCoverageBlur,
    deleteRow,
    openEditModal,
    closeEditModal,
    handleAreaTypeChange,
    handleModalSave,
    handleTableChange,
    handlePageChange
  };
};

export default useCropZonesTableState;
